import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { plannerAPI, sundayOf, ymd, addDays, formatTime } from "@/lib/planner";
import { toast } from "sonner";
import { EventDialog } from "@/pages/planner/PlannerCalendar";

export default function PlannerWeek() {
  const [weekStart, setWeekStart] = useState(sundayOf(new Date()));
  const [data, setData] = useState({ events: [], by_day: {}, start: "" });
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);

  const load = async (w = weekStart) => {
    setLoading(true);
    try {
      const d = await plannerAPI.week(ymd(w));
      setData(d);
    } catch { toast.error("Couldn't load the week."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const del = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await plannerAPI.deleteEvent(ev.id);
      await load();
      toast.success("Deleted.");
    } catch { toast.error("Couldn't delete."); }
  };

  const weekEnd = addDays(weekStart, 6);
  const label = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;

  const shift = (n) => setWeekStart(addDays(weekStart, n * 7));

  return (
    <div data-testid="planner-week">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">THIS WEEK</div>
          <h1 className="text-3xl sm:text-4xl text-[#2A2823] mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{label}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button aria-label="Previous week" data-testid="planner-week-prev" onClick={() => shift(-1)} className="w-9 h-9 rounded-full border border-[#A87F56]/30 flex items-center justify-center hover:bg-[#A87F56]/10">
            <ChevronLeft className="w-4 h-4 text-[#3E4A37]" />
          </button>
          <button data-testid="planner-week-today" onClick={() => setWeekStart(sundayOf(new Date()))} className="text-sm rounded-full border border-[#A87F56]/30 px-4 h-9 text-[#3E4A37] hover:bg-[#A87F56]/10">
            This week
          </button>
          <button aria-label="Next week" data-testid="planner-week-next" onClick={() => shift(1)} className="w-9 h-9 rounded-full border border-[#A87F56]/30 flex items-center justify-center hover:bg-[#A87F56]/10">
            <ChevronRight className="w-4 h-4 text-[#3E4A37]" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-[#A87F56]/25">
        {loading ? (
          <div className="py-8 text-center text-sm text-[#2A2823]/50">Loading…</div>
        ) : (
          <div className="space-y-4">
            {[...Array(7)].map((_, i) => {
              const d = addDays(weekStart, i);
              const key = ymd(d);
              const events = data.by_day?.[key] || [];
              const isToday = key === ymd(new Date());
              return (
                <div key={key} className="flex gap-4 pb-4 border-b border-[#A87F56]/15 last:border-b-0" data-testid={`planner-week-row-${key}`}>
                  <div className="w-20 shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#2A2823]/50">
                      {d.toLocaleDateString(undefined, { weekday: "short" })}
                    </div>
                    <div className={`mt-1 text-2xl ${isToday ? "text-[#3E4A37] font-medium" : "text-[#2A2823]"}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      {d.getDate()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    {events.length === 0 ? (
                      <Link
                        to={`/planner/calendar?add=1&date=${key}`}
                        data-testid={`planner-week-add-${key}`}
                        className="text-sm italic text-[#2A2823]/40 hover:text-[#3E4A37]"
                      >
                        + Add event
                      </Link>
                    ) : (
                      <ul className="space-y-1.5">
                        {events.map((e) => (
                          <li key={e.id} className="group flex items-center gap-3 text-sm text-[#2A2823]">
                            <span className="text-xs tabular-nums text-[#2A2823]/60 w-16 shrink-0">
                              {e.all_day ? "All day" : formatTime(e.start_at)}
                            </span>
                            <span className={`flex-1 min-w-0 truncate ${e.done_at ? "line-through text-[#2A2823]/40" : ""}`}>
                              {e.title}
                            </span>
                            <button
                              onClick={() => setEditingEvent(e)}
                              aria-label="Edit event"
                              data-testid={`planner-week-edit-${e.id}`}
                              className="text-[#2A2823]/35 hover:text-[#3E4A37] opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => del(e)}
                              aria-label="Delete event"
                              data-testid={`planner-week-delete-${e.id}`}
                              className="text-[#2A2823]/35 hover:text-[#8a3a2a] opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <EventDialog
        open={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        defaultDate={ymd(new Date())}
        event={editingEvent}
        onSaved={() => { setEditingEvent(null); load(); }}
      />
    </div>
  );
}
